import { useMutation, useQuery } from "@tanstack/react-query";

import type { AxiosError, AxiosResponse } from "axios";

import { useMutate } from "@/shared/hooks";
import { useApi } from "@/shared/api/api";
import { useToast } from "@/shared/hooks/useToast";
import type { Mentor } from "@/entities/mentor/model";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { PaginatedResponse, PaginationRequest } from "@/shared/types";
import type { PauseRequest, UpdateContactsRequest } from "./api.dto";

export const useUpdateContacts = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "patch",
    url: "/mentors/contacts",
    toast: {
      success: {
        description: "Вы обновили свои контактные данные.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    UpdateContactsRequest,
    Response
  >({
    mutationKey: ["updateContacts"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const usePauseMentorAccountByUserId = ({
  userId,
}: {
  userId: string;
}) => {
  const { mutationFn } = useMutate({
    method: "patch",
    url: `/mentors/user/${userId}/pause`,
  });
  const { toast } = useToast();

  return useMutation<AxiosResponse, AxiosError, PauseRequest, Response>({
    mutationKey: ["pauseMentorByUserId"],
    mutationFn,
    onSuccess: (data, variables) => {
      toast({
        title: "Удачно!",
        description: !variables.isActive
          ? "Вы поставили ментора на паузу"
          : "Вы возобновили ментора в выдаче",
      });
      return data;
    },
  });
};

export const usePauseMentorAccount = ({ userId }: { userId: string }) => {
  const { mutationFn } = useMutate({
    method: "patch",
    url: `/mentors/user/${userId}/pause`,
  });
  const { toast } = useToast();

  return useMutation<AxiosResponse, AxiosError, PauseRequest, Response>({
    mutationKey: ["pauseMentor"],
    mutationFn,
    onSuccess: (data, variables) => {
      toast({
        title: "Удачно!",
        description: !variables.isActive
          ? "Вы возобновили свою работу"
          : "Вы приостановили свою работу",
      });
      return data;
    },
  });
};

export const useGetMentor = ({ userId }: { userId: string }) => {
  const { api } = useApi();

  return useQuery<{ data: Mentor }>({
    queryKey: ["mentor", userId],
    queryFn: async () => {
      const { data } = await api.get(`/mentors/user/${userId}`);

      return data;
    },
  });
};

export const useMentors = (
  props: PaginationRequest & { isTrafficManager?: boolean; isMaster?: boolean },
) => {
  const { api } = useApi();

  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Mentor>>({
    queryKey: ["mentors", params],
    queryFn: async () => {
      const response = await api.get("/mentors", {
        params: filterObjectKeys(params),
      });

      return response.data;
    },
  });
};
